import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  useListPtoSmrRegistersQuery,
  useImportPtoSmrRegisterMutation,
  useListPtoSmrItemsQuery,
  useUpdatePtoSmrItemMutation,
  useSyncPtoSmrRegisterMutation,
  useRelinkPtoSmrActsMutation,
  useSetPtoSmrItemActMutation,
  useListPtoExecutiveActsQuery,
  type PtoSmrRegister,
  type PtoSmrItem,
  type PtoExecutiveAct,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 340px) 1fr;
  gap: ${({ theme }) => theme.spacing(5)};

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(4)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const RegItem = styled.button<{ $active: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) => ($active ? theme.colors.primarySoft : theme.colors.surface)};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  cursor: pointer;
  font: inherit;
  color: inherit;
`;

const Select = styled.select`
  padding: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
`;

const TableWrap = styled.div`
  overflow: auto;
  max-height: 70vh;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 12px;

  th,
  td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    padding: 5px 7px;
    text-align: left;
    white-space: nowrap;
  }
  th {
    position: sticky;
    top: 0;
    background: ${({ theme }) => theme.colors.backgroundElevated};
    font-weight: 600;
  }
  tr[data-review='true'] td {
    background: rgba(220, 50, 50, 0.06);
  }
`;

const num = (v: number | null) => (v === null || v === undefined ? '' : String(v));
const numOrUndef = (s: string) => (s.trim() === '' ? undefined : Number(s));

const actStatusLabel: Record<string, string> = {
  draft: 'черновик',
  on_signing: 'на подписи',
  signed: 'подписан',
  submitted: 'сдан',
};

export function PtoSmrRegisters() {
  const { projectId = '' } = useParams();
  const { can_write } = useAgentPermission('pto');
  const { data: regsData, isLoading } = useListPtoSmrRegistersQuery(projectId, { skip: !projectId });
  const [importReg, { isLoading: importing }] = useImportPtoSmrRegisterMutation();
  const registers: PtoSmrRegister[] = regsData?.items ?? [];

  const [selected, setSelected] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onImport(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError('Приложите файл ведомости (.xlsx).');
      return;
    }
    const form = new FormData();
    if (title.trim()) form.append('title', title.trim());
    form.append('file', file);
    try {
      const res = await importReg({ projectId, form }).unwrap();
      setSelected(res.register.id);
      setTitle('');
      setFile(null);
    } catch {
      setError('Не удалось импортировать .xlsx. Проверьте, что это отчёт СМР, сохранённый как .xlsx.');
    }
  }

  return (
    <div>
      <Row>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Накопительная ведомость СМР</h1>
          <Muted>Импорт отчёта СМР по объекту (.xlsx) — вид работ × титул × ось × конструкция</Muted>
        </div>
        <Link to={`/agents/pto/${projectId}`}>
          <Button $variant="secondary">← К проекту</Button>
        </Link>
      </Row>

      <Columns>
        <div>
          {can_write && (
            <Panel as="form" onSubmit={onImport} style={{ marginBottom: 16 }}>
              <h3 style={{ marginTop: 0 }}>Импорт ведомости</h3>
              <Field>
                Название (опц. — по умолчанию имя файла)
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Отчёт СМР по ДГП-2" />
              </Field>
              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                style={{ marginBottom: 12 }}
              />
              <div>
                <Button $variant="primary" type="submit" disabled={importing || !file}>
                  {importing ? 'Импорт…' : 'Импортировать .xlsx'}
                </Button>
              </div>
              {error && <Muted style={{ color: 'crimson', marginTop: 8 }}>{error}</Muted>}
            </Panel>
          )}

          <h3 style={{ marginTop: 0 }}>Ведомости</h3>
          {isLoading ? (
            <Muted>Загрузка…</Muted>
          ) : registers.length === 0 ? (
            <Muted>Ведомостей пока нет. Импортируйте .xlsx.</Muted>
          ) : (
            registers.map((r) => (
              <RegItem key={r.id} $active={selected === r.id} onClick={() => setSelected(r.id)}>
                <div style={{ fontWeight: 600 }}>{r.title}</div>
                <Muted style={{ fontSize: 12 }}>
                  {r.item_count} строк{r.source_filename ? ` · ${r.source_filename}` : ''}
                </Muted>
              </RegItem>
            ))
          )}
        </div>

        <div>
          {selected ? (
            <RegisterItems registerId={selected} projectId={projectId} canWrite={can_write} />
          ) : (
            <Muted>Выберите ведомость слева, чтобы увидеть строки.</Muted>
          )}
        </div>
      </Columns>
    </div>
  );
}

function RegisterItems({
  registerId,
  projectId,
  canWrite,
}: {
  registerId: string;
  projectId: string;
  canWrite: boolean;
}) {
  const { data, isLoading } = useListPtoSmrItemsQuery(registerId);
  const [updateItem] = useUpdatePtoSmrItemMutation();
  const [syncReg, { isLoading: syncing }] = useSyncPtoSmrRegisterMutation();
  const [relinkActs, { isLoading: relinking }] = useRelinkPtoSmrActsMutation();
  const [setItemAct] = useSetPtoSmrItemActMutation();
  const { data: actsData } = useListPtoExecutiveActsQuery({ projectId }, { skip: !projectId });
  const acts: PtoExecutiveAct[] = useMemo(() => actsData?.items ?? [], [actsData]);
  const items: PtoSmrItem[] = useMemo(() => data?.items ?? [], [data]);

  const [tFilter, setTFilter] = useState('');
  const [wFilter, setWFilter] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<PtoSmrItem>>({});
  const [draftActId, setDraftActId] = useState<string | null>(null);

  async function onSync(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append('file', f);
    try {
      await syncReg({ id: registerId, form }).unwrap();
    } catch {
      /* проигнорировать — пользователь повторит */
    }
    e.target.value = '';
  }

  const tituls = useMemo(
    () => Array.from(new Set(items.map((i) => i.titul).filter(Boolean))) as string[],
    [items],
  );
  const works = useMemo(
    () => Array.from(new Set(items.map((i) => i.work_type).filter(Boolean))) as string[],
    [items],
  );
  const filtered = useMemo(
    () =>
      items.filter(
        (i) => (!tFilter || i.titul === tFilter) && (!wFilter || i.work_type === wFilter),
      ),
    [items, tFilter, wFilter],
  );

  function startEdit(i: PtoSmrItem) {
    setEditing(i.id);
    setDraftActId(i.act_id);
    setDraft({
      work_type: i.work_type,
      titul: i.titul,
      axis: i.axis,
      construction: i.construction,
      qty_rd: i.qty_rd,
      qty_executed: i.qty_executed,
      qty_remainder: i.qty_remainder,
      aosr_no: i.aosr_no,
      aosr_status: i.aosr_status,
      status: i.status,
      needs_review: i.needs_review,
    });
  }
  async function save(i: PtoSmrItem) {
    await updateItem({ id: i.id, patch: draft }).unwrap();
    if ((draftActId ?? null) !== (i.act_id ?? null)) {
      await setItemAct({ id: i.id, act_id: draftActId }).unwrap();
    }
    setEditing(null);
    setDraft({});
  }

  if (isLoading) return <Muted>Загрузка строк…</Muted>;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <Select value={tFilter} onChange={(e) => setTFilter(e.target.value)}>
          <option value="">— все титулы —</option>
          {tituls.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
        <Select value={wFilter} onChange={(e) => setWFilter(e.target.value)}>
          <option value="">— все работы —</option>
          {works.map((w) => (
            <option key={w} value={w}>
              {w}
            </option>
          ))}
        </Select>
        <Muted style={{ alignSelf: 'center' }}>
          Показано {filtered.length} из {items.length}
        </Muted>
        {canWrite && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            <span style={{ opacity: 0.8 }}>{syncing ? 'Синхронизация…' : 'Обновить из .xlsx:'}</span>
            <input type="file" accept=".xlsx" onChange={onSync} disabled={syncing} />
          </label>
        )}
        {canWrite && (
          <Button
            $variant="secondary"
            $size="sm"
            type="button"
            disabled={relinking}
            onClick={() => relinkActs(registerId)}
          >
            {relinking ? 'Связка…' : 'Пересвязать акты'}
          </Button>
        )}
      </div>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Работа</th>
              <th>Титул</th>
              <th>Ось</th>
              <th>Констр.</th>
              <th>по РД</th>
              <th>Факт</th>
              <th>Остаток</th>
              <th>№ АОСР</th>
              <th>Статус (файл)</th>
              <th>Акт ИД (M4)</th>
              {canWrite && <th></th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((i) => {
              const ed = editing === i.id;
              return (
                <tr key={i.id} data-review={i.needs_review}>
                  <td>{i.work_type}</td>
                  <td>{i.titul}</td>
                  <td>{i.axis}</td>
                  <td>{i.construction}</td>
                  <td>
                    {ed ? (
                      <input
                        style={{ width: 64 }}
                        defaultValue={num(i.qty_rd)}
                        onChange={(e) => setDraft((d) => ({ ...d, qty_rd: numOrUndef(e.target.value) }))}
                      />
                    ) : (
                      num(i.qty_rd)
                    )}
                  </td>
                  <td>
                    {ed ? (
                      <input
                        style={{ width: 64 }}
                        defaultValue={num(i.qty_executed)}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, qty_executed: numOrUndef(e.target.value) }))
                        }
                      />
                    ) : (
                      num(i.qty_executed)
                    )}
                  </td>
                  <td>
                    {ed ? (
                      <input
                        style={{ width: 64 }}
                        defaultValue={num(i.qty_remainder)}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, qty_remainder: numOrUndef(e.target.value) }))
                        }
                      />
                    ) : (
                      num(i.qty_remainder)
                    )}
                  </td>
                  <td>
                    {ed ? (
                      <input
                        style={{ width: 130 }}
                        defaultValue={i.aosr_no ?? ''}
                        onChange={(e) => setDraft((d) => ({ ...d, aosr_no: e.target.value }))}
                      />
                    ) : (
                      i.aosr_no
                    )}
                  </td>
                  <td>
                    {ed ? (
                      <input
                        style={{ width: 90 }}
                        defaultValue={i.aosr_status ?? ''}
                        onChange={(e) => setDraft((d) => ({ ...d, aosr_status: e.target.value }))}
                      />
                    ) : (
                      i.aosr_status
                    )}
                  </td>
                  <td>
                    {ed ? (
                      <Select
                        value={draftActId ?? ''}
                        onChange={(e) => setDraftActId(e.target.value || null)}
                        style={{ fontSize: 11, padding: '2px 4px' }}
                      >
                        <option value="">— не связан —</option>
                        {acts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {(a.kind || '').toUpperCase()} {a.act_no ?? ''} ·{' '}
                            {actStatusLabel[a.status] ?? a.status}
                          </option>
                        ))}
                      </Select>
                    ) : i.act_status ? (
                      <span title={`${i.act_kind ?? ''} ${i.act_no_linked ?? ''}`}>
                        ● {actStatusLabel[i.act_status] ?? i.act_status}
                      </span>
                    ) : (
                      <span style={{ opacity: 0.6 }}>{i.aosr_no ? 'не связан' : '—'}</span>
                    )}
                  </td>
                  {canWrite && (
                    <td>
                      {ed ? (
                        <span style={{ display: 'flex', gap: 4 }}>
                          <Button $variant="primary" $size="sm" type="button" onClick={() => save(i)}>
                            ОК
                          </Button>
                          <Button $variant="ghost" $size="sm" type="button" onClick={() => setEditing(null)}>
                            ✕
                          </Button>
                        </span>
                      ) : (
                        <Button $variant="ghost" $size="sm" type="button" onClick={() => startEdit(i)}>
                          ✎
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>
    </div>
  );
}
